import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, 
  TrendingUp, 
  Users, 
  Activity,
  Bell,
  RefreshCw,
  Home,
  ChevronRight,
  BarChart3,
  Clock,
  Sparkles,
  ArrowRight,
  Plus,
  Zap,
  Target,
  Star
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import PageContainer from '@/components/common/PageContainer';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

// Import dashboard widgets
import UpcomingSessionsWidget from '@/components/dashboard/UpcomingSessionsWidget';
import PendingCheckoutsWidget from '@/components/dashboard/PendingCheckoutsWidget';
import NewSubscriptionsWidget from '@/components/dashboard/NewSubscriptionsWidget';
import ExpiringSubscriptionsWidget from '@/components/dashboard/ExpiringSubscriptionsWidget';
import ExpiredSubscriptionsWidget from '@/components/dashboard/ExpiredSubscriptionsWidget';

// Import data service
import { fetchDashboardStats, DashboardStats } from '@/services/dashboard';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [userProfile, setUserProfile] = useState<any>(null);
  const today = new Date();

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await fetchDashboardStats();
      setDashboardData(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar os dados do dashboard. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('first_name')
            .eq('id', user.id)
            .single();
          
          if (!error && data) {
            setUserProfile(data);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    
    fetchUserProfile();
  }, [user]);

  const handleRefresh = () => {
    loadDashboardData();
    toast({
      title: "Dados atualizados",
      description: "Dashboard atualizado com sucesso.",
    });
  };

  // Stats cards data
  const getStatsCards = () => {
    if (!dashboardData) return [];

    return [
      {
        title: "Próximas Sessões",
        value: dashboardData.upcomingSessions.length.toString(),
        icon: Calendar,
        description: "Sessões agendadas",
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        onClick: () => navigate('/classes')
      },
      {
        title: "Check-outs Pendentes",
        value: dashboardData.pendingCheckouts.length.toString(),
        icon: Clock,
        description: "Necessitam check-out",
        color: "text-orange-600",
        bgColor: "bg-orange-50",
        onClick: () => navigate('/classes')
      },
      {
        title: "Novas Assinaturas",
        value: dashboardData.newSubscriptions.length.toString(),
        icon: TrendingUp,
        description: "Últimos 7 dias",
        color: "text-green-600",
        bgColor: "bg-green-50",
        onClick: () => navigate('/subscriptions')
      },
      {
        title: "Vencendo em 60 dias",
        value: dashboardData.subscriptionsExpiringSoon.length.toString(),
        icon: Activity,
        description: "Precisam atenção",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        onClick: () => navigate('/subscriptions')
      }
    ];
  };

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Página Inicial</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header with Gradient Background */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path 
                  fill="rgba(255, 255, 255, 0.25)" 
                  d="M28.31,502.06l-22.83-33.16c2.83-1.95,5.72-3.95,8.66-6,29.97-20.81,67.28-46.72,119.43-65.67,35.27-12.82,72.85-20.86,113.48-24.23-6.13-3.25-12.27-6.69-18.3-10.34-19.51-11.8-35.53-24.3-47.63-37.15-15.6-16.58-25.21-34.28-28.58-52.62-.7-3.82-1.29-7.59-1.76-11.3-11.77-1.09-23.46-3.87-34.84-8.32-29.68-11.61-57.34-34.34-82.21-67.54-13.34-17.81-22.86-38.24-28.3-60.72C1.09,107.05-.66,87.72.22,67.56,1.7,33.97,10.17,7.36,12.73,0l38.02,13.24c-2.11,6.05-9.07,27.97-10.31,56.09-1.6,36.48,6.98,67.53,25.51,92.26,20.31,27.11,42.06,45.34,64.66,54.18,6.26,2.45,12.56,4.16,18.88,5.14,1.32-20.67,5.43-38.28,9.63-51.53,9.97-31.47,26.22-57.23,45.76-72.54,9.06-7.1,18.58-11.71,28.29-13.72,11.03-2.28,22.22-1.07,32.37,3.51,11.52,5.2,20.52,13.57,26.04,24.2,4.97,9.58,7.07,20.63,6.23,32.85-1.49,21.74-12.62,46.21-30.53,67.15-18.86,22.04-43.6,38.42-69.67,46.12-2.19.65-4.38,1.23-6.58,1.76.32,2.31.69,4.61,1.11,6.89,1.95,10.6,8.11,21.47,18.31,32.31,9.59,10.19,22.76,20.38,39.14,30.29,30.76,18.6,65.94,31.97,84.83,39.15,5.09,1.93,9.11,3.46,12.1,4.72,2.14.9,3.69,1.63,5.03,2.37,10.47,5.81,12.4,15.94,10.86,22.84-1.65,7.43-8.22,15.26-19.16,16.01-1.57.11-3.22.08-5.37-.08-15.91,1.19-31.04,1.75-45.41,1.75-133.69,0-203.21,48.29-255.36,84.5-2.99,2.08-5.92,4.11-8.8-6.09ZM244.08,122.3c-4.25,0-9.06,2.09-14.37,6.25-12.84,10.05-24.88,29.86-32.2,52.99-3.62,11.43-6.04,23.4-7.24,35.51,17.22-5.98,33.57-17.36,46.43-32.38,12.23-14.29,20.06-30.63,20.95-43.72.91-13.29-5.8-16.32-8.67,17.61-1.53-.69-3.17-1.04-4.89-1.04Z"
                />
                <path 
                  fill="rgba(255, 255, 255, 0.25)" 
                  d="M491.98,0l-22.83,33.16c2.83,1.95,5.72,3.95,8.66,6,29.97,20.81,67.28,46.72,119.43,65.67,35.27,12.82,72.85,20.86,113.48,24.23-6.13,3.25-12.27,6.69-18.3,10.34-19.51,11.8-35.53,24.3-47.63,37.15-15.6,16.58-25.21,34.28-28.58,52.62-.7,3.82-1.29,7.59-1.76,11.3-11.77,1.09-23.46,3.87-34.84,8.32-29.68,11.61-57.34,34.34-82.21,67.54-13.34,17.81-22.86,38.24-28.3,60.72-4.34,17.96-6.1,37.29-5.21,57.45,1.48,33.59,9.95,60.2,12.51,67.56l38.02-13.24c-2.11-6.05-9.07-27.97-10.31-56.09-1.6-36.48,6.98-67.53,25.51-92.26,20.31-27.11,42.06-45.34,64.66-54.18,6.26-2.45,12.56-4.16,18.88-5.14,1.32,20.67,5.43,38.28,9.63,51.53,9.97,31.47,26.22,57.23,45.76-72.54,9.06,7.1,18.58-11.71,28.29-13.72,11.03,2.28,22.22,1.07,32.37-3.51,11.52-5.2,20.52-13.57,26.04-24.2,4.97-9.58,7.07-20.63,6.23-32.85-1.49,21.74-12.62,46.21-30.53,67.15-18.86,22.04-43.6,38.42-69.67,46.12-2.19-.65-4.38-1.23-6.58-1.76.32-2.31.69-4.61,1.11-6.89,1.95-10.6,8.11-21.47,18.31,32.31,9.59,10.19,22.76,20.38,39.14-30.29,30.76-18.6,65.94,31.97,84.83-39.15,5.09-1.93,9.11,3.46,12.1-4.72,2.14-.9,3.69-1.63,5.03-2.37,10.47-5.81,12.4-15.94,10.86-22.84-1.65-7.43-8.22,15.26-19.16-16.01-1.57-.11-3.22-.08-5.37.08-15.91,1.19-31.04,1.75-45.41,1.75-133.69,0-203.21-48.29-255.36-84.5-2.99-2.08-5.92-4.11-8.8-6.09ZM707.74,379.76c-4.25,0-9.06-2.09-14.37-6.25-12.84-10.05-24.88-29.86-32.2-52.99-3.62-11.43-6.04-23.4-7.24-35.51,17.22,5.98,33.57,17.36,46.43,32.38,12.23,14.29,20.06,30.63,20.95,43.72.91,13.29-5.8,16.32-8.67,17.61-1.53.69-3.17,1.04-4.89,1.04Z"
                />
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/10"></div>
          
          <div className="relative container mx-auto px-6 py-8 pb-12">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-extrabold text-white">
                Olá {userProfile?.first_name || user?.user_metadata?.first_name || 'Usuário'} 👋
              </h1>
                    <p className="text-blue-100 text-base">
                      Acompanhe suas métricas e gerencie suas operações em tempo real
                    </p>
                  </div>
            </div>
          </div>

              <div className="flex items-center gap-3">
                <Badge 
                  className="bg-white/20 text-white border-white/30 backdrop-blur-sm px-3 py-1"
                >
                  <Activity className="h-3 w-3 mr-1" />
                  Última atualização: {format(lastUpdated, 'HH:mm', { locale: ptBR })}
                </Badge>
            <Button
                  variant="secondary"
                  size="default"
              onClick={handleRefresh}
              disabled={loading}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
            >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
            </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-6 relative z-10 space-y-8">

            {/* Enhanced Stats Cards with Modern Design */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {getStatsCards().map((stat, index) => {
                const cardIndex = index % 4; // Ensure we stay within bounds
                const gradientMap = [
                  'from-blue-50 to-white',
                  'from-orange-50 to-white', 
                  'from-green-50 to-white',
                  'from-yellow-50 to-white'
                ];
                const circleColorMap = [
                  'from-blue-500/10 to-transparent',
                  'from-orange-500/10 to-transparent',
                  'from-green-500/10 to-transparent', 
                  'from-yellow-500/10 to-transparent'
                ];
                const iconBgMap = [
                  'bg-blue-100',
                  'bg-orange-100',
                  'bg-green-100',
                  'bg-yellow-100'
                ];
                const textColorMap = [
                  'text-blue-600',
                  'text-orange-600', 
                  'text-green-600',
                  'text-yellow-600'
                ];

                                return (
            <Card 
              key={index} 
                    className={`relative overflow-hidden border shadow-sm bg-gradient-to-br ${gradientMap[cardIndex]} cursor-pointer transition-all duration-300 transform hover:-translate-y-1`}
              onClick={stat.onClick}
            >
                    <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${circleColorMap[cardIndex]} rounded-full transform translate-x-10 -translate-y-10`} />
                    <CardContent className="p-6 relative z-10">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 ${iconBgMap[cardIndex]} rounded-lg`}>
                            <stat.icon className={`h-5 w-5 ${textColorMap[cardIndex]}`} />
                          </div>
                          <span className="text-sm font-extrabold text-gray-600">{stat.title}</span>
                        </div>
                        <div className="text-3xl font-extrabold text-gray-900">
                          {stat.value}
                        </div>
                </div>
                <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                      {stat.description}
                        </div>
                        <div className="flex items-center gap-1">
                          <ArrowRight className={`h-4 w-4 ${textColorMap[cardIndex]} group-hover:translate-x-1 transition-transform`} />
                  </div>
                </div>
              </CardContent>
            </Card>
                );
              })}
        </div>

            {/* Featured Upcoming Sessions Section */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
        <div>
                      <CardTitle className="text-lg">Próximas Sessões</CardTitle>
                      <CardDescription>
                        Sessões agendadas que precisam da sua atenção
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                    {dashboardData?.upcomingSessions?.length || 0} sessões
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
          <UpcomingSessionsWidget 
            sessions={dashboardData?.upcomingSessions || []}
            loading={loading}
          />
              </CardContent>
            </Card>

            {/* Main Dashboard Grid with Enhanced Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Checkouts */}
              <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Clock className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Check-outs Pendentes</CardTitle>
                        <CardDescription>
                          Sessões que necessitam finalização
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-orange-50 text-orange-700 border-orange-200">
                      {dashboardData?.pendingCheckouts?.length || 0} pendentes
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="h-[350px]">
            <PendingCheckoutsWidget 
              checkouts={dashboardData?.pendingCheckouts || []}
              loading={loading}
            />
                </CardContent>
              </Card>

          {/* New Subscriptions */}
              <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Novas Assinaturas</CardTitle>
                        <CardDescription>
                          Assinaturas criadas nos últimos 7 dias
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
                      {dashboardData?.newSubscriptions?.length || 0} novas
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="h-[350px]">
            <NewSubscriptionsWidget 
              subscriptions={dashboardData?.newSubscriptions || []}
              loading={loading}
            />
                </CardContent>
              </Card>
        </div>

        {/* Subscription Management Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expiring Subscriptions */}
              <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Activity className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Vencendo em 60 dias</CardTitle>
                        <CardDescription>
                          Assinaturas que precisam de atenção
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                      {dashboardData?.subscriptionsExpiringSoon?.length || 0} vencendo
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="h-[400px]">
            <ExpiringSubscriptionsWidget 
              subscriptions={dashboardData?.subscriptionsExpiringSoon || []}
              loading={loading}
            />
                </CardContent>
              </Card>

          {/* Expired Subscriptions */}
              <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Target className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Assinaturas Vencidas</CardTitle>
                        <CardDescription>
                          Requerem ação imediata
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="destructive" className="bg-red-50 text-red-700 border-red-200">
                      {dashboardData?.expiredSubscriptions?.length || 0} vencidas
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="h-[400px]">
            <ExpiredSubscriptionsWidget 
              subscriptions={dashboardData?.expiredSubscriptions || []}
              loading={loading}
            />
                </CardContent>
              </Card>
        </div>

            {/* Enhanced Quick Actions Section */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Zap className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Ações Rápidas</CardTitle>
                      <CardDescription>
                        Acesse rapidamente as funcionalidades mais usadas
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200">
                    <Star className="h-3 w-3 mr-1" />
                    Favoritos
                  </Badge>
                </div>
          </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card
                    className="group cursor-pointer border shadow-sm bg-gradient-to-br from-blue-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                onClick={() => navigate('/class-wizard')}
              >
                    <CardContent className="p-6 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                          <Calendar className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-gray-900">Nova Classe</h3>
                          <p className="text-xs text-gray-500 mt-1">Criar nova turma</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    className="group cursor-pointer border shadow-sm bg-gradient-to-br from-green-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                onClick={() => navigate('/subscription-wizard')}
              >
                    <CardContent className="p-6 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors">
                          <TrendingUp className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-gray-900">Nova Assinatura</h3>
                          <p className="text-xs text-gray-500 mt-1">Cadastrar cliente</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    className="group cursor-pointer border shadow-sm bg-gradient-to-br from-purple-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                onClick={() => navigate('/customers')}
              >
                    <CardContent className="p-6 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                          <Users className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-gray-900">Gerenciar Clientes</h3>
                          <p className="text-xs text-gray-500 mt-1">Ver todos os clientes</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    className="group cursor-pointer border shadow-sm bg-gradient-to-br from-orange-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                onClick={() => navigate('/dashboard')}
              >
                    <CardContent className="p-6 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-3 bg-orange-100 rounded-xl group-hover:bg-orange-200 transition-colors">
                          <BarChart3 className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-gray-900">Dashboard Completo</h3>
                          <p className="text-xs text-gray-500 mt-1">Ver todas as métricas</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
            </div>
          </CardContent>
        </Card>

            {/* Bottom Spacing */}
            <div className="h-16" />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
